
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';
import { usePoints } from '@/hooks/usePoints';

interface ReferralCodeDisplayProps {
  referralCode: string;
  referredCount?: number;
}

const ReferralCodeDisplay: React.FC<ReferralCodeDisplayProps> = ({ 
  referralCode, 
  referredCount = 0 
}) => {
  const [inputReferralCode, setInputReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { processReferral } = usePoints();

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied to clipboard!');
  };

  const handleUseReferralCode = async () => {
    if (!inputReferralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setLoading(true);
    const success = await processReferral(inputReferralCode.trim());
    
    if (success) {
      setInputReferralCode('');
    }
    
    setLoading(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Your Referral Code */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-sports-green flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Your Referral Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              value={referralCode}
              readOnly
              className="bg-gray-700 border-gray-600 text-white font-mono text-lg text-center"
            />
            <Button
              onClick={copyReferralCode}
              size="sm"
              className="bg-sports-green text-black hover:bg-green-600"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-gray-400 text-sm">
            Share this code with friends to earn 100 points when they sign up!
          </div>
          <div className="flex items-center gap-2 text-sports-green">
            <Users className="h-4 w-4" />
            <span className="text-sm">{referredCount} friends referred</span>
          </div>
        </CardContent>
      </Card>

      {/* Use Referral Code */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-blue-400 flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Have a Referral Code?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="referral-input" className="text-white">
              Enter Referral Code
            </Label>
            <Input
              id="referral-input"
              value={inputReferralCode}
              onChange={(e) => setInputReferralCode(e.target.value.toUpperCase())}
              placeholder="Enter code here..."
              className="bg-gray-700 border-gray-600 text-white font-mono text-center"
              maxLength={8}
            />
          </div>
          <Button
            onClick={handleUseReferralCode}
            disabled={loading || !inputReferralCode.trim()}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            {loading ? 'Processing...' : 'Redeem Code (+50 points)'}
          </Button>
          <div className="text-gray-400 text-sm">
            Use a friend's referral code to get 50 bonus points!
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralCodeDisplay;
